import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { BoxProps, Tooltip } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { Avatar, PreviewContainer, Spinner } from 'components';
import { useSubscription, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { User } from 'types';
import { getFullName } from 'utils';

import { EmployeeRow, EmployeesListW, SeeMoreLink } from './styled';

interface EmployeeListPreviewProps {
  isUsersListLoading: boolean;
  usersList: User[];
  linkInviteEmployee: string;
  linkOfListEmployees: string;
  wrapperSx?: BoxProps['sx'];
}

export function EmployeeListPreview({
  isUsersListLoading,
  usersList,
  linkInviteEmployee,
  linkOfListEmployees,
}: EmployeeListPreviewProps): ReactElement {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();
  const { isTrial } = useSubscription();

  const titleLink = pathname.includes('resources')
    ? Links.resources.employees.index
    : Links.we.employees.index;

  const employeeDetailLink = pathname.includes('resources')
    ? Links.resources.employees.employee.index
    : Links.we.employees.employee.index;

  const inviteEmployeeLink = isTrial ? '' : linkInviteEmployee;

  return (
    <PreviewContainer
      title={t('we.employeeList.title')}
      buttonTitle={t('we.employeeList.action')}
      handleTitleClick={() => navigate(titleLink)}
      addIconLink={inviteEmployeeLink}
      maximizeIconLink={linkOfListEmployees}
      containerSx={{ padding: '0px', backgroundColor: '#fbfbfb' }}
      headerSx={{ marginBottom: isMobile ? '16px' : '40px' }}
    >
      <EmployeesListW>
        {isUsersListLoading && <Spinner size={24} />}
        {usersList.slice(0, 5).map(({ id, firstName, lastName, email, avatar }) => {
          const fullName = getFullName({ firstName, lastName });
          const tooltipTitle = fullName || email;

          return (
            <Tooltip key={id} title={tooltipTitle}>
              <EmployeeRow
                onClick={() => navigate(generatePath(employeeDetailLink, { employeeId: id }))}
              >
                <Avatar userId={id} filename={avatar?.small || ''} />
                <p>{fullName || firstName}</p>
                <KeyboardArrowRightIcon />
              </EmployeeRow>
            </Tooltip>
          );
        })}
        <SeeMoreLink to={titleLink}>{t('we.employeeList.seeMore')}</SeeMoreLink>
      </EmployeesListW>
    </PreviewContainer>
  );
}
